import { useState, useEffect } from 'react';
import type { FunctionComponent } from 'react';
import { NumberInput } from '@mantine/core';
import type { NumberInputProps } from '@mantine/core';
import { SECONDARY_PRICES } from 'constants/menuManagement';

import { ClearInputButton } from 'components/MenuManagement/ClearInputButton';
import { RIGHT_SECTION_WIDTH } from 'components/MenuManagement/inputConstants';
import { useTranslation } from 'i18n';

const NS = 'menuManagement';

// Stable sentinel for identity checks — decoupled from the translated copy so copy edits
// never break the comparison logic below.
const SAME_AS_DEFAULT_PRICE = 'SAME_AS_DEFAULT_PRICE';

type PriceInputProps = Omit<NumberInputProps, 'value' | 'onChange' | 'onBlur'> & {
  record: Record<string, unknown>;
  priceType?: string;
  setPrice?: (value: number | null) => void;
  value?: number | string | null;
};

export const PriceInput: FunctionComponent<PriceInputProps> = ({
  record,
  priceType = 'price',
  setPrice = () => {},
  label = '',
  value,
  disabled,
  ...props
}) => {
  const { t } = useTranslation(NS);
  const sameAsDefaultPriceLabel = t('PriceInput.sameAsDefaultPrice');
  const [displayValue, setDisplayValue] = useState<string | null>(null);

  const formatValue = (val: string | number | null): string | null => {
    if (val === null || val === '') return null;
    if (Number.isNaN(Number(val))) return null;
    return (Number(val) / 100).toFixed(2);
  };

  useEffect(() => {
    if (priceType === SECONDARY_PRICES.key && (record?.price === value || value === null)) {
      setDisplayValue(SAME_AS_DEFAULT_PRICE);
    } else {
      setDisplayValue(formatValue(value ?? null));
    }
  }, [value, record?.price, priceType]);

  const handlePriceChange = (newValue: number | string | null) => {
    if (newValue === '' || newValue === null) {
      if (priceType === SECONDARY_PRICES.key) {
        setDisplayValue(SAME_AS_DEFAULT_PRICE);
        setPrice(null);
      } else {
        setDisplayValue(null);
        setPrice(null);
      }
      return;
    }

    if (newValue !== sameAsDefaultPriceLabel) {
      setDisplayValue(String(newValue));
    }
  };

  const handleBlur = () => {
    if (displayValue && displayValue !== SAME_AS_DEFAULT_PRICE) {
      const formattedValue = Number(displayValue).toFixed(2);
      const numericValue = Math.round(parseFloat(formattedValue) * 100);
      setPrice(numericValue);
      setDisplayValue(formattedValue);
    }
  };

  const handleClearClick = () => {
    handlePriceChange(null);
  };

  return (
    <NumberInput
      // eslint-disable-next-line react/jsx-props-no-spreading
      {...props}
      disabled={disabled}
      label={label}
      value={
        displayValue === SAME_AS_DEFAULT_PRICE ? sameAsDefaultPriceLabel : (displayValue ?? '')
      }
      onChange={handlePriceChange}
      onBlur={handleBlur}
      decimalScale={2}
      prefix="$"
      allowNegative={false}
      placeholder={priceType === SECONDARY_PRICES.key ? sameAsDefaultPriceLabel : undefined}
      rightSectionWidth={RIGHT_SECTION_WIDTH}
      rightSection={
        <ClearInputButton
          onClick={handleClearClick}
          show={Boolean(displayValue && displayValue !== SAME_AS_DEFAULT_PRICE && !disabled)}
        />
      }
      mb="0"
      styles={{
        input: {
          fontSize: 'var(--mantine-font-size-md)',
        },
      }}
    />
  );
};
